import React, { useState } from 'react';
import { format, isToday, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, CalendarRange, GripVertical } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { STATUS_CONFIG } from './constants';

// Timeline hours from 7:00 to 15:00 with 15-minute intervals
const TIMELINE_HOURS = [
  '07:00', '07:15', '07:30', '07:45',
  '08:00', '08:15', '08:30', '08:45',
  '09:00', '09:15', '09:30', '09:45',
  '10:00', '10:15', '10:30', '10:45',
  '11:00', '11:15', '11:30', '11:45',
  '12:00', '12:15', '12:30', '12:45',
  '13:00', '13:15', '13:30', '13:45',
  '14:00', '14:15', '14:30', '14:45',
  '15:00', '15:15', '15:30'
];

// Get appointments that start in a specific 15-minute slot
const getAppointmentsForSlot = (appointments, slot) => {
  return appointments.filter(appt => appt.hora_inicio === slot);
};

const WeekView = ({
  weekDays,
  weekAppointments,
  selectedDate,
  onDaySelect,
  onAppointmentClick,
  onAppointmentReschedule,
  onPrevWeek,
  onNextWeek,
  onToday
}) => {
  const [draggedAppointment, setDraggedAppointment] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  // Handle drag start
  const handleDragStart = (e, appointment) => {
    setDraggedAppointment(appointment);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', appointment.id);
    // Add a slight delay to allow the drag image to be set
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  // Handle drag end
  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedAppointment(null);
    setDropTarget(null);
  };

  // Handle drag over (allow drop)
  const handleDragOver = (e, dateStr, hour) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget({ dateStr, hour });
  };

  // Handle drag leave
  const handleDragLeave = () => {
    setDropTarget(null);
  };

  // Handle drop
  const handleDrop = (e, dateStr, hour) => {
    e.preventDefault();
    setDropTarget(null);
    
    if (!draggedAppointment || !onAppointmentReschedule) return;
    
    // Calculate new times
    const oldDuration = calculateDuration(draggedAppointment.hora_inicio, draggedAppointment.hora_fin);
    const newHoraInicio = hour;
    const newHoraFin = addMinutesToTime(hour, oldDuration);
    
    // Check if end time is valid (before 15:30)
    if (newHoraFin > '15:30') {
      return; // Don't allow drop if it would end after 15:30
    }
    
    // Call the reschedule handler
    onAppointmentReschedule(draggedAppointment.id, {
      date: dateStr,
      hora_inicio: newHoraInicio,
      hora_fin: newHoraFin
    });
    
    setDraggedAppointment(null);
  };

  // Calculate duration in minutes between two times
  const calculateDuration = (start, end) => {
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);
    return (endH * 60 + endM) - (startH * 60 + startM);
  };

  // Add minutes to a time string
  const addMinutesToTime = (time, minutes) => {
    const [h, m] = time.split(':').map(Number);
    const totalMinutes = h * 60 + m + minutes;
    const newH = Math.floor(totalMinutes / 60);
    const newM = totalMinutes % 60;
    return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
  };

  return (
    <Card className="bg-white border-slate-100 shadow-soft">
      <CardHeader className="pb-4 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <CalendarRange className="w-5 h-5 text-brand-main" strokeWidth={1.5} />
            Semana del {format(weekDays[0], "d 'de' MMMM", { locale: es })} al {format(weekDays[6], "d 'de' MMMM yyyy", { locale: es })}
          </CardTitle>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <GripVertical className="w-3 h-3" />
              Arrastra para reprogramar
            </span>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="icon"
                onClick={onPrevWeek}
                className="border-slate-200 hover:bg-slate-100"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={onToday}
                className="border-slate-200 hover:bg-slate-100"
              >
                Hoy
              </Button>
              <Button 
                variant="outline" 
                size="icon"
                onClick={onNextWeek}
                className="border-slate-200 hover:bg-slate-100"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0 overflow-hidden">
        {/* Week Header with day names */}
        <div className="grid grid-cols-[50px_repeat(7,1fr)] border-b border-slate-200 bg-white sticky top-0 z-10">
          <div className="p-2 border-r border-slate-200 bg-slate-50" /> {/* Empty corner */}
          {weekDays.map((day) => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const dayAppts = weekAppointments[dateStr] || [];
            const isDayToday = isToday(day);
            const isSelected = isSameDay(day, selectedDate);
            
            return (
              <div 
                key={dateStr}
                className={`p-2 text-center border-r border-slate-200 cursor-pointer transition-colors ${
                  isDayToday 
                    ? 'bg-[#00b4d8] text-white' 
                    : isSelected
                    ? 'bg-[#1a5192] text-white'
                    : 'bg-slate-50 hover:bg-slate-100'
                }`}
                onClick={() => onDaySelect(day)}
              >
                <p className="text-xs font-medium uppercase">
                  {format(day, 'EEE', { locale: es })}
                </p>
                <p className="text-xl font-bold">
                  {format(day, 'd')}
                </p>
                {dayAppts.length > 0 && (
                  <Badge className={`text-[10px] mt-1 ${isDayToday || isSelected ? 'bg-white/20 text-white border-white/30' : 'bg-[#1a5192]/10 text-[#1a5192]'}`}>
                    {dayAppts.length} cita{dayAppts.length !== 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Timeline Grid */}
        <div className="max-h-[500px] overflow-y-auto">
          {TIMELINE_HOURS.map((hour, hourIndex) => {
            const isFullHour = hour.endsWith(':00');
            return (
            <div key={hour} className={`grid grid-cols-[50px_repeat(7,1fr)] ${isFullHour ? 'border-b border-slate-200' : 'border-b border-slate-50'}`}>
              {/* Time label - only show for full hours */}
              <div className={`min-h-[40px] border-r border-slate-200 p-0.5 text-right pr-2 bg-slate-50/50 flex items-start justify-end`}>
                <span className={`text-[10px] font-medium ${isFullHour ? 'text-slate-600' : 'text-slate-300'}`}>
                  {hour}
                </span>
              </div>
              
              {/* Day cells for this slot */}
              {weekDays.map((day) => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const dayAppts = weekAppointments[dateStr] || [];
                const hourAppts = getAppointmentsForSlot(dayAppts, hour);
                const isDayToday = isToday(day);
                const isDropTarget = dropTarget?.dateStr === dateStr && dropTarget?.hour === hour;
                
                return (
                  <div 
                    key={`${dateStr}-${hour}`}
                    className={`min-h-[40px] border-r border-slate-100 p-0.5 transition-colors overflow-hidden ${
                      isDayToday ? 'bg-[#00b4d8]/5' : ''
                    } ${isFullHour ? 'bg-slate-50/20' : ''} ${
                      isDropTarget ? 'bg-blue-100 ring-2 ring-blue-400 ring-inset' : ''
                    }`}
                    onDragOver={(e) => handleDragOver(e, dateStr, hour)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, dateStr, hour)}
                  >
                    {hourAppts.map((appt) => {
                      // Don't allow dragging completed or cancelled appointments
                      const canDrag = !['COMPLETADA', 'CANCELADA'].includes(appt.status);
                      
                      return (
                        <div
                          key={appt.id}
                          draggable={canDrag}
                          onDragStart={(e) => canDrag && handleDragStart(e, appt)}
                          onDragEnd={handleDragEnd}
                          className={`rounded p-1.5 cursor-pointer transition-all hover:shadow-md text-xs mb-0.5 border-l-[3px] w-full box-border ${
                            canDrag ? 'cursor-grab active:cursor-grabbing' : ''
                          } ${
                            appt.status === 'COMPLETADA' ? 'bg-emerald-50 border-emerald-500 hover:bg-emerald-100' :
                            appt.status === 'CANCELADA' ? 'bg-red-50 border-red-500 hover:bg-red-100' :
                            appt.status === 'NO_ASISTIO' ? 'bg-amber-50 border-amber-500 hover:bg-amber-100' :
                            appt.status === 'REPROGRAMADA' ? 'bg-purple-50 border-purple-500 hover:bg-purple-100' :
                            'bg-blue-50 border-blue-500 hover:bg-blue-100'
                          } ${
                            draggedAppointment?.id === appt.id ? 'ring-2 ring-blue-500' : ''
                          }`}
                          onClick={() => onAppointmentClick(appt)}
                          data-testid={`week-appointment-${appt.id}`}
                        >
                          <div className="flex items-start gap-1">
                            {canDrag && (
                              <GripVertical className="w-3 h-3 text-slate-400 flex-shrink-0 mt-0.5" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-mono text-[10px] text-slate-600 leading-tight">
                                {appt.hora_inicio} - {appt.hora_fin}
                              </p>
                              <p className="font-semibold text-[11px] text-slate-800 truncate leading-tight">
                                {appt.patient_name}
                              </p>
                              <p className="text-[10px] text-slate-500 truncate leading-tight">
                                {appt.tipo_examen}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default WeekView;
